import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box, Grid, styled, Typography } from "@mui/material";
import DrawerCustom from "../../../shared/general/Drawer";
import TextFieldTheme from "../../../shared/general/TextFieldTheme";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DragHandleRoundedIcon from "@mui/icons-material/DragHandleRounded";
import ButtonBlue from "../../../shared/general/ButtonBlue";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { useTranslation } from "react-i18next";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { getGroupLevelByIdCompany, updateGroupLevelByIdCompany } from "../../../../../actions/company";
import { openNotificationAlert } from "../../../../../actions/notificationAlert";

const StyledRoot = styled(Box)({
  padding: "16px",
  "& .MuiAutocomplete-root": {
    "& .MuiOutlinedInput-root": {
      padding: "13.5px 14px",
      paddingRight: "32px",
      "& input": {
        padding: 0,
      },
    },
  },
  "& .MuiCheckbox-root": {
    "&.Mui-checked": {
      color: "#46cbe2",
    },
  },
});

const getItemStyle = (isDragging, draggableStyle) => ({
  display: "flex",
  userSelect: "none",
  alignItems: "center",
  ...draggableStyle,
});

const DialogGroupLevel = (props) => {

  const { drawerConfig, handleClose, company } = props;

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const validateYupSchema = yup.object({
    groupLevel: yup.array().of(
      yup.object().shape({
        groupLevelName: yup.string().required("โปรดกรอกข้อมูล"),
      })
    ),
  });

  const useHookForm = useForm({
    defaultValues: {
      groupLevel: [],
    },
    resolver: yupResolver(validateYupSchema),
    mode: "all",
  })

  const groupLevelFieldArray = useFieldArray({
    control: useHookForm.control,
    name: "groupLevel",
  }); 

  useEffect(() => {

    if(drawerConfig.isOpen){
      useHookForm.clearErrors();
      useHookForm.setValue("groupLevel", drawerConfig.data.groupLevel, { shouldValidate: true, shouldDirty: false, shouldTouch: false })
    }

  }, [drawerConfig.isOpen]);

  const onDragGroupEnd = (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    groupLevelFieldArray.move(source.index, destination.index);
  }

  const onSubmit = async (data) => {

    const cleansingData = data.groupLevel.map((item, index) => ({
      idGroupLevel: item.idGroupLevel,
      groupLevelName: item.groupLevelName,
      idCompany: item.idCompany,
      indexGroupLevel: index+1,
    }))

    const result = await dispatch(updateGroupLevelByIdCompany(company.idCompany, cleansingData));

    if(result && result.status === 200) {
      handleClose();
      dispatch(openNotificationAlert({
        type: "success",
        message: t("DataSaveSuccessful"),
      }));
      dispatch(getGroupLevelByIdCompany(company.idCompany));
    } else {
      dispatch(openNotificationAlert({
        type: "error",
        message: t("AnErrorOccurred")
      }));
    }
  }

  const handleAddGroupLevel = () => {
    groupLevelFieldArray.append({
      idGroupLevel: null,
      groupLevelName: "",
      idCompany: company.idCompany,
    })
  }

  return (
    <DrawerCustom
      title={t("AddEditLevelGroup")}
      anchor={"right"}
      open={drawerConfig.isOpen}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "500px",
        },
      }}
    >
      <StyledRoot>
        <form onSubmit={useHookForm.handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} container justifyContent="space-between">
              <Typography color="text.third" fontWeight="600">{t(`LevelGroup`)}</Typography>
              <ButtonBlue
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={handleAddGroupLevel}
              >
                {t("AddLevelGroup")}
              </ButtonBlue>
            </Grid>

            <Grid item xs={12}>
              <DragDropContext onDragEnd={onDragGroupEnd}>
                <Droppable droppableId="droppable-group-level">
                  {(provided, _) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      <Grid container spacing={2}>
                        {groupLevelFieldArray.fields.map((field, index) => (
                          <Draggable
                            key={field.id}
                            // draggableId={field.indexGroupLevel.toString()}
                            draggableId={field.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Grid 
                                item xs={12}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                container
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                <DragHandleRoundedIcon />
                                <Controller
                                  name={`groupLevel.${index}.groupLevelName`}
                                  control={useHookForm.control}
                                  render={({field, fieldState}) => (
                                    <TextFieldTheme
                                      {...field}
                                      style={{ marginLeft: 8, flexGrow: 1, width: "unset" }}
                                      error={fieldState.error? true: false}
                                      helperText={fieldState.error? fieldState.error.message: null}
                                    />
                                  )}
                                />
                              </Grid>
                            )}
                          </Draggable>
                        ))}
                      </Grid>
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>

            <Grid item xs={12} container justifyContent="space-between">
              <ButtonBlue
                onClick={handleClose}
              >
                {t("Cancel")}
              </ButtonBlue>
              <ButtonBlue
                variant="contained"
                type="submit"
              >
                {t("Save")}
              </ButtonBlue>
            </Grid>
          </Grid>
        </form>
      </StyledRoot>
    </DrawerCustom>
  );
};

export default DialogGroupLevel;